import { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { Button, Switch, DatePicker, Form, Input, message, Modal, Row, Steps, Space, Table, Divider, Tooltip } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { getProjestsApi, getScoreStagesListApi, addScoreStagestApi, updateScoreStagestApi, deleteScoreStagestApi, updateCerificateApi, reportStateUpdateApi, judgesAuthUpdateApi } from '@services/Selection';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import './index.less';
import StageTaskModal from './StageTask';
import PageHeader from '@components/PageHeader/PageHeader';
import { workspaceSelectionReviewPersonnelPath, workspaceSelectionReviewTopicsPath } from '@/layouts/selectionWorkspacePaths';

const ScoreStageList = () => {
    const dateFormat = 'YYYY-MM-DD HH:mm';
    const params = useParams();
    const navigate = useNavigate();
    const pid = ~~params?.pid || 0;
    const location = useLocation();

    // 列表数据
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creatMarkModal, setCreatMarkModal] = useState(false);
    const [markName, setMarkName] = useState('');
    const [currentId, setCurrentId] = useState();

    const [stageId, setStageId] = useState();
    const [title, setTitle] = useState('');
    const [projectInfo, setProjectInfo] = useState(null);

    const [form] = Form.useForm();

    const [openStageTaskModal, setOpenStageTaskModal] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const getData = () => {
        getScoreStagesListApi({ projectId: pid }).then(res => {
            setLoading(false);
            if (res && res.status_code === 200) {
                setData(res.data);
            }
        });
    };

    // const getProjestDetail = () => {
    //     getProjestsApi({ id: pid }).then((res) => {
    //         if (res && res.status_code === 200) {
    //             setTitleName(res.data.name);
    //         }
    //     })
    // }

    useEffect(() => {
        setLoading(true);
        // getProjestDetail();
        getData();
    }, []);

    // 获取项目基本信息用于 PageHeader 展示
    useEffect(() => {
        if (pid && !projectInfo) {
            getProjestsApi({ id: pid }).then(res => {
                if (res.status_code === 200) {
                    setProjectInfo(res.data);
                }
            });
        }
    }, [pid]);

    useEffect(() => {
        document.title = '评议管理-医评议';
    }, []);

    // 点击创建
    const handleClickMark = () => {
        setMarkName('');
        setCreatMarkModal(true);
    };

    // 点击编辑
    const handleEdit = v => {
        setMarkName(v.name);
        setCurrentId(v.id);
        setCreatMarkModal(true);
        form.setFieldValue('name', v.name);
    };

    // 点击删除
    const handleDeletd = row => {
        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle" />,
            content: `确认是否删除环节：${row.name}`,
            centered: true,
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                setLoading(true);
                deleteScoreStagestApi({ id: row.id }).then(res => {
                    if (res && res.status_code === 200) {
                        message.success('删除成功');
                        getData();
                    } else {
                        setLoading(false);
                    }
                });
            },
        });
    };

    // 创建&编辑弹窗
    const handleCreatMark = () => {
        form.validateFields().then(values => {
            if (currentId) {
                updateScoreStagestApi({
                    id: currentId,
                    name: values.name,
                    dateTime: values.dateTime ? `${dayjs(values.dateTime[0]).format(dateFormat)}~${dayjs(values.dateTime[1]).format(dateFormat)}` : '',
                    projectId: pid,
                }).then(res => {
                    setCurrentId();
                    setLoading(true);
                    if (res && res.status_code === 200) {
                        getData();
                        setCreatMarkModal(false);
                    } else {
                        setLoading(false);
                    }
                });
            } else {
                addScoreStagestApi({
                    name: values.name,
                    dateTime: values.dateTime ? `${dayjs(values.dateTime[0]).format(dateFormat)}~${dayjs(values.dateTime[1]).format(dateFormat)}` : '',
                    projectId: pid,
                }).then(res => {
                    setLoading(true);
                    setCurrentId();
                    if (res && res.status_code === 200) {
                        getData();
                        setCreatMarkModal(false);
                    } else {
                        setLoading(false);
                    }
                });
            }
        });
    };

    const handleCancelCreat = () => {
        setCreatMarkModal(false);
        setMarkName('');
        setCurrentId();
    };

    // 阶段评议
    const handleChangeStageScore = (bool, id) => {
        setLoading(true);
        updateScoreStagestApi({
            id: id,
            projectId: pid,
            scoreState: bool ? 1 : 0,
        }).then(res => {
            if (res && res.status_code === 200) {
                getData();
            } else {
                setLoading(false);
            }
        });
    };

    // 评议专家点评
    const handleChangeJudgeReview = (bool, id) => {
        setLoading(true);
        updateScoreStagestApi({
            id: id,
            projectId: pid,
            reviewsState: bool ? 1 : 0,
        }).then(res => {
            if (res && res.status_code === 200) {
                getData();
            } else {
                setLoading(false);
            }
        });
    };

    // 评议报告
    const handleChangeCaseReport = (bool, id) => {
        setLoading(true);
        reportStateUpdateApi({
            id: id,
            projectId: pid,
            reportState: bool ? 1 : 0,
        }).then(res => {
            if (res && res.status_code === 200) {
                getData();
            } else {
                setLoading(false);
            }
        });
    };

    // 专家认证开关
    const handleChangeJudgesAuth = (bool, id) => {
        setLoading(true);
        judgesAuthUpdateApi({
            id: id,
            projectId: pid,
            judgesAuth: bool ? 1 : 0,
        }).then(res => {
            if (res && res.status_code === 200) {
                getData();
            } else {
                setLoading(false);
            }
        });
    };

    // 完赛证书
    const handleChangeCerificate = (bool, id) => {
        setLoading(true);
        updateCerificateApi({
            id: id,
            projectId: pid,
            rankState: bool ? 1 : 0,
        }).then(res => {
            if (res && res.status_code === 200) {
                getData();
            } else {
                setLoading(false);
            }
        });
    };

    // 阶段任务
    const openStageTask = row => {
        setOpenStageTaskModal(true);
        setTitle(row.name);
        setStageId(row.id);
    };

    const columns = [
        {
            title: '评议阶段',
            dataIndex: 'name',
            width: 200,
        },
        {
            title: '专家认证',
            dataIndex: 'judgesAuth',
            width: 80,
            render: (_, row) => {
                return <Switch checkedChildren="开启" unCheckedChildren="关闭" checked={row.judgesAuth === 1} onChange={bool => handleChangeJudgesAuth(bool, row.id)} />;
            },
        },
        {
            title: '专家任务数',
            width: 100,
            dataIndex: 'judgeTaskCount',
        },
        {
            title: '实践代表任务数',
            width: 120,
            dataIndex: 'playerTaskCount',
        },
        {
            title: '评议分数',
            dataIndex: 'scoreState',
            width: 80,
            render: (_, row) => {
                return <Switch checkedChildren="显示" unCheckedChildren="隐藏" checked={row.scoreState} onChange={bool => handleChangeStageScore(bool, row.id)} />;
            },
        },
        {
            title: '专家点评',
            dataIndex: 'reviewsState',
            width: 80,
            render: (_, row) => {
                return <Switch checkedChildren="显示" unCheckedChildren="隐藏" checked={row.reviewsState} onChange={bool => handleChangeJudgeReview(bool, row.id)} />;
            },
        },
        {
            title: '评议报告',
            dataIndex: 'reportState',
            width: 80,
            render: (_, row) => {
                return <Switch checkedChildren="显示" unCheckedChildren="隐藏" checked={row.reportState} onChange={bool => handleChangeCaseReport(bool, row.id)} />;
            },
        },
        {
            title: '结果证书',
            dataIndex: 'rankState',
            width: 80,
            render: (_, row) => {
                return <Switch checkedChildren="显示" unCheckedChildren="隐藏" checked={row.rankState} onChange={bool => handleChangeCerificate(bool, row.id)} />;
            },
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 340,
            render: (_, row) => {
                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={() => handleEdit(row)}>
                            编辑
                        </Button>
                        <Button size="small" type="link" onClick={() => openStageTask(row)}>
                            阶段任务
                        </Button>
                        <Button size="small" type="link" onClick={() => navigate(workspaceSelectionReviewPersonnelPath(pid, row.id), { state: { ...location.state, stageName: row.name } })}>
                            人员管理
                        </Button>
                        <Button size="small" type="link" onClick={() => navigate(workspaceSelectionReviewTopicsPath(pid, row.id), { state: { ...location.state, stageName: row.name } })}>
                            评议实践
                        </Button>
                        <Button size="small" type="link" onClick={() => handleDeletd(row)}>
                            删除
                        </Button>
                    </Space>
                );
            },
        },
    ];

    return (
        <div className="page-container list-page-container selection-score-stage-page">
            <PageHeader title="评议管理" breadcrumb={[{ label: '医评议', path: '/selection/projects' }, { label: projectInfo?.name }, { label: '评议管理' }]} />

            <div className="step-container">
                <Steps current="0" titlePlacement="vertical" items={[{ title: '创建评议阶段' }, { title: '设置阶段任务' }, { title: '添加人员管理' }, { title: '添加评议实践' }]} />
            </div>

            <div ref={tableRegionRef} className="table-region-container selection-score-stage-table-region">
                <div className="main-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={handleClickMark}>
                                创建评议阶段
                            </Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={getData} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap selection-score-stage-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always marks-table' : 'marks-table'}
                        rowKey={item => item.id}
                        bordered
                        size="medium"
                        loading={loading}
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                    />
                </div>
            </div>

            <Modal
                className="mark-change-modal"
                title={currentId ? '编辑评议阶段' : '创建评议阶段'}
                open={creatMarkModal}
                centered
                onOk={handleCreatMark}
                mask={{ closable: false }}
                onCancel={handleCancelCreat}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleCancelCreat}>取消</Button>
                            <Button type="primary" onClick={handleCreatMark}>
                                确定
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Form
                    form={form}
                    name="createForm"
                    labelCol={{
                        span: 6,
                    }}
                    wrapperCol={{
                        span: 16,
                    }}
                    autoComplete="off"
                >
                    <Form.Item
                        label="评议阶段名称："
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: '评议阶段名称不能为空',
                            },
                        ]}
                    >
                        <Input placeholder="请输入评议阶段名称" maxLength={20} />
                    </Form.Item>
                </Form>
            </Modal>

            <StageTaskModal openStageTaskModal={openStageTaskModal} setOpenStageTaskModal={setOpenStageTaskModal} stageId={stageId} title={title} />
        </div>
    );
};

export default ScoreStageList;
